import { features, solution } from '@/lib/marketing-data';
import { cn } from '@/lib/utils';
import {
  GitBranch,
  Layers,
  FileText,
  Shield,
  Network,
  MessageSquare,
  FolderGit2,
  Puzzle,
} from 'lucide-react';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  GitBranch,
  Layers,
  FileText,
  Shield,
  Network,
  MessageSquare,
  FolderGit2,
  Puzzle,
};

const tierColors: Record<string, string> = {
  free: 'bg-green-500/10 text-green-700', // Fixed: contrast 4.79:1 (was 3.02:1)
  pro: 'bg-blue-100 text-blue-700', // Fixed: contrast 5.74:1 (was 4.48:1)
  enterprise: 'bg-purple-500/10 text-purple-700', // Fixed: contrast improved
};

const tierLabels: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

export function Features() {
  return (
    <section className="py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Everything You Need to Ship Better Software
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Powerful features designed to accelerate your development workflow and
            keep your documentation in sync.
          </p>
        </div>

        {/* Features Grid */}
        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => {
            const Icon = iconMap[feature.icon] || Layers;
            return (
              <div
                key={feature.title}
                className="group relative rounded-xl border bg-card p-6 transition-all hover:border-primary/50 hover:shadow-lg"
              >
                {/* Tier Badge */}
                <div
                  className={cn(
                    'absolute top-4 right-4 rounded-full px-2 py-0.5 text-xs font-medium',
                    tierColors[feature.tier]
                  )}
                >
                  {tierLabels[feature.tier]}
                </div>

                {/* Icon */}
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/20">
                  <Icon className="h-6 w-6 text-primary" />
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold">{feature.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <section className="py-16 sm:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold text-primary uppercase tracking-wide">
            How It Works
          </span>
          <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
            {solution.title}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {solution.description}
          </p>
        </div>

        {/* Steps */}
        <div className="mt-16 grid gap-8 lg:grid-cols-4">
          {solution.steps.map((step, index) => (
            <div key={step.number} className="relative">
              {/* Connector Line */}
              {index < solution.steps.length - 1 && (
                <div className="absolute top-12 left-[calc(50%+2rem)] hidden w-[calc(100%-4rem)] border-t-2 border-dashed border-border lg:block" />
              )}

              <div className="flex flex-col items-center text-center">
                {/* Step Number */}
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-2xl font-bold relative z-10">
                  {step.number}
                </div>

                {/* Content */}
                <h3 className="mt-6 text-xl font-semibold">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Problems() {
  return (
    <section className="py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold text-destructive uppercase tracking-wide">
            The Problem
          </span>
          <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
            Sound Familiar?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Engineering teams everywhere struggle with the same challenges.
          </p>
        </div>

        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: 'Documentation Decay',
              description:
                'Your docs are always out of sync. PRDs are stale before the sprint ends.',
              icon: '📄',
            },
            {
              title: 'Knowledge Silos',
              description:
                "Critical context lives in one engineer's head. Onboarding takes months.",
              icon: '🧠',
            },
            {
              title: 'Analysis Paralysis',
              description:
                'Weeks spent understanding legacy code before making a single change.',
              icon: '⏰',
            },
            {
              title: 'Scaling Bottleneck',
              description:
                "Growth stalls because your architecture can't keep pace with your ambition.",
              icon: '📉',
            },
          ].map((problem) => (
            <div
              key={problem.title}
              className="rounded-xl border border-destructive/20 bg-destructive/5 p-6"
            >
              <span className="text-3xl">{problem.icon}</span>
              <h3 className="mt-4 text-lg font-semibold">{problem.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {problem.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
